import type {
  ChatSelectedTemplateItem,
  SuggestTemplatesToolResult,
} from "schemas/generated/chatMessage";

import React from "react";

import { useChatId } from "@editor/components/editor/ai/chat/hooks/useChatId";
import PageTemplateCard, {
  PageTemplateCardSkeleton,
} from "@editor/components/shops-ui/home/PageTemplateCard";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { chatRouter, chatUtils } from "@editor/trpc/chat";
import { fileTemplateRouter } from "@editor/trpc/fileTemplate";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";

import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";

import { useIsChatLoading } from "../store/ChatStoreContext";

function SelectedTemplate({ templateId }: { templateId: string }) {
  const projectId = useCurrentProjectId();
  const { data: template, isLoading } = fileTemplateRouter.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );

  if (!template) {
    return null;
  }

  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  return <PageTemplateCard template={template} />;
}

function SuggestedTemplateCard({
  templateId,
  toolResult,
  toolMessageId,
}: {
  templateId: string;
  toolResult: SuggestTemplatesToolResult | null;
  toolMessageId?: string;
}) {
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const { openModal } = useModal();
  const chatId = useChatId();
  const isChatLoading = useIsChatLoading(chatId);
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");
  const { data: template, isLoading } = fileTemplateRouter.get.useQuery(
    projectId
      ? {
          projectId,
          id: templateId,
        }
      : skipToken,
  );
  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    nextJSProjectRouter.createPageFromTemplate.useMutation({
      onSuccess: (data) => {
        handleUseCreatedFileFromTemplate(data.createdFile.id);
      },
    });

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      void chatUtils.get.invalidate({
        chatId: chatId!,
        projectId,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  const handleUseCreatedFileFromTemplate = async (fileId: string) => {
    void nextJSProjectUtils.listPages.invalidate({ projectId });

    if (!isServerSandboxEnabled) {
      // NOTE (Gabe 2025-08-08): We await the updatePageFilesInWebContainer call to ensure that
      // the page is available for rendering before navigating.
      await updatePageFilesInWebContainer({
        pages: [{ id: fileId }],
      });
    }

    if (template && projectId && chatId && toolResult && toolMessageId) {
      updateMessage({
        id: toolMessageId,
        projectId,
        content: [
          {
            type: "tool-result",
            toolCallId: toolResult.toolCallId,
            toolName: toolResult.toolName,
            output: {
              type: "json",
              value: {
                ...toolResult?.output.value,
                selectedTemplateId: templateId,
              },
            },
          },
        ],
      });

      const selectedTemplateItem: ChatSelectedTemplateItem = {
        type: "selectedTemplate",
        selectedTemplateId: templateId,
        selectedTemplateName: template.name,
      };

      addMessage({
        projectId,
        chatId,
        content: [
          {
            type: "text",
            contentType: "selectedTemplate" as const,
            text: JSON.stringify(selectedTemplateItem),
            fileId,
          },
        ],
        role: "user",
      });
    }
  };

  const handlePreviewClick = () => {
    if (!template) {
      return;
    }

    openModal({
      type: "pageTemplatePreviewModal",
      props: {
        template,
        chatId: chatId ?? undefined,
        onSuccess: (fileId) => {
          handleUseCreatedFileFromTemplate(fileId);
        },
      },
    });
  };

  const handleUseTemplateClick = (templateId: string) => {
    if (projectId) {
      logEvent("template.use", {
        origin: "chat",
        templateId,
      });
      createPageFromTemplate({
        projectId,
        templateId,
      });
    }
  };

  if (isLoading) {
    return <PageTemplateCardSkeleton />;
  }

  if (!template) {
    return null;
  }

  return (
    <div className="relative group w-full h-full">
      <PageTemplateCard template={template} />
      <div
        className={twMerge(
          "absolute inset-0 flex items-center justify-center rounded-xl transition-opacity duration-200 bg-white/40 opacity-0 group-hover:opacity-100",
        )}
        aria-hidden="true"
      >
        <div className="flex flex-col gap-3 items-center w-2/3">
          <Button
            variant="primary"
            size="base"
            onClick={() => handleUseTemplateClick(template.id)}
            startEnhancer={
              <PlusIcon
                className={twMerge("w-4 h-4", isChatLoading && "hidden")}
              />
            }
            layoutClassName="w-full"
            isLoading={isCreatingPage || isChatLoading}
            disabled={isCreatingPage || isChatLoading}
          >
            Use Template
          </Button>
          <Button
            variant="secondary"
            size="base"
            onClick={handlePreviewClick}
            layoutClassName="w-full"
            disabled={isCreatingPage}
          >
            Preview
          </Button>
        </div>
      </div>
    </div>
  );
}

function SuggestedTemplates({
  toolResult,
  toolMessageId,
}: {
  toolResult: SuggestTemplatesToolResult | null;
  toolMessageId?: string;
}) {
  if (!toolResult) {
    return <div>Fetching templates...</div>;
  }

  if (!toolResult.output.value.suggestedTemplateIds) {
    return <div>No templates found</div>;
  }

  const selectedTemplateId = toolResult.output.value.selectedTemplateId;

  if (selectedTemplateId) {
    return (
      <div className="w-2/3">
        <SelectedTemplate templateId={selectedTemplateId} />
      </div>
    );
  }

  return (
    <div
      className="grid gap-4 w-full"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}
    >
      {toolResult.output.value.suggestedTemplateIds.map((templateId) => (
        <SuggestedTemplateCard
          key={templateId}
          templateId={templateId}
          toolResult={toolResult}
          toolMessageId={toolMessageId}
        />
      ))}
    </div>
  );
}

export default SuggestedTemplates;
